import { useQuery } from '@apollo/react-hooks';
import styled from '@emotion/styled';
import { navigate, RouteComponentProps } from '@reach/router';
import { orderDetailQuery } from 'graphql/queries';
import { getPurchaseRevenue } from 'lib/discount';
import { OrderDetailQuery, OrderDetailQueryVariables, PaymentType } from 'graphql/types';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import colors from 'lib/colors';
import { isNotNull } from 'lib/helpers';
import { groupBy } from 'ramda';
import React from 'react';
import checked from '../assets/checked.svg';
import Box from './Box';
import Flex from './Flex';
import PageWrapper from './PageWrapper';
import Text from './Text';

interface IProps {
  orderId?: string;
}

const OrderPlaced: React.FC<RouteComponentProps<IProps>> = props => {
  const { pushToDataLayer } = useGoogleAnalytics();

  useQuery<OrderDetailQuery, OrderDetailQueryVariables>(orderDetailQuery, {
    variables: { id: Number(props.orderId) },
    skip: !props?.orderId,
    onCompleted: data => {
      const order = data.order;
      if (null === order) return;

      let shipping = 0;
      const shippingPa = order.payments?.filter(pa => pa?.type === PaymentType.DELIVERY);
      if (shippingPa && shippingPa[0]) {
        shipping = shippingPa[0].amount;
      }

      const price = order.price || 0;
      const { revenue, tax } = getPurchaseRevenue(price, shipping, order.discount?.discount || 0);
      const catalogItems = order.sets?.flatMap(set => set?.catalogItems) || [];
      const caItemsById = groupBy(ca => ca.id.toString(), catalogItems.filter(isNotNull));

      pushToDataLayer({
        event: 'formSent',
        formType: 'reservation',
      });

      pushToDataLayer({
        event: 'purchase',
        ecommerce: {
          currencyCode: 'CZK',
          purchase: {
            actionField: {
              id: order?.id,
              revenue,
              tax: tax,
              shipping: shipping,
              ...(order.discountCode ? { coupon: order.discountCode.code } : {}),
            },
            products: Object.entries(caItemsById).map(([, items]) => ({
              name: items[0]?.name,
              id: items[0]?.id.toString(),
              price: items[0]?.price?.toString() || '0',
              brand: items[0]?.producer || '',
              category: items[0]?.categoryName || '',
              variant: items[0]?.variant || '',
              quantity: items?.length,
            })),
          },
        },
      });

      setTimeout(() => navigate('/'), 5000);
    },
    onError: () => setTimeout(() => navigate('/'), 5000),
  });

  return (
    <PageWrapper hideFooter simpleHeader>
      <Flex mt={6} justifyContent="center" flexDirection="column">
        <Flex mt={4} alignItems="center" flexDirection="column">
          <CentralBox>
            <Image src={checked} alt="checked" />
            <br />
            <Text fontSize={[19, 24]} color={colors.white}>
              Platba proběhla v pořádku,{' '}
            </Text>
            <Text fontSize={[19, 24]} color={colors.white}>
              děkujeme za platbu
            </Text>
          </CentralBox>
          <Box mt={4}>
            <Text fontSize={[15]}>Potvrzení Vám bylo zasláno na Váš e-mail</Text>
          </Box>
        </Flex>
      </Flex>
    </PageWrapper>
  );
};

export default OrderPlaced;

const CentralBox = styled.div({
  display: 'flex',
  backgroundColor: colors.green,
  padding: '60px',
  alignItems: 'center',
  flexDirection: 'column',
});

const Image = styled.img({
  width: 'auto',
  height: 64,
  objectFit: 'contain',
});
